"use client";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Carousel, Image } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { UserData } from "@/lib/api/types";
import dynamic from "next/dynamic";
import { track } from "@vercel/analytics";
import { getLargeSrc } from "@/lib/helpers";
import LazyVideo from "@/app/components/LazyVideo";
import styles from "./styles.module.scss";

import { usePhotoEngagement } from "@/lib/hooks/usePhotoEngagement";

const ReactPlayer = dynamic(() => import("react-player"), { ssr: false });

/**
 * Custom carousel arrow. react-slick clones this element and injects
 * `className`, `style` and `onClick`; we only forward those so the AntD
 * default chevron (a top-left-anchored pseudo element) is replaced with a
 * properly centered icon.
 */
const SliderArrow = ({
  className,
  style,
  onClick,
  icon,
  label,
}: {
  className?: string;
  style?: React.CSSProperties;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  icon: React.ReactNode;
  label: string;
}) => (
  <button
    type="button"
    className={className}
    style={style}
    onClick={onClick}
    aria-label={label}
  >
    {icon}
  </button>
);

interface DisplaySliderProps {
  user: {
    name: string;
    _id: string;
  };
  photos: UserData["photos"];
  slides: number;
  /** Break out of the container padding to span the full viewport width. */
  fullBleed?: boolean;
}

const sliderDefaults = {
  dots: false,
  infinite: true,
  speed: 500,
  slidesToScroll: 1,
  arrows: true,
  // antd v6 Carousel defaults `draggable` to false, disabling mouse-drag
  // navigation. Restore react-slick's old default so dragging works on desktop.
  draggable: true,
};

const DisplaySlider = ({
  user,
  photos,
  slides,
  fullBleed,
}: DisplaySliderProps) => {
  const [photoId, setPhotoId] = useState<string | null>(null);

  const { root, trackOpen } = usePhotoEngagement();
  const selectedPhoto = photos.find((photo) => photo._id === photoId);
  useEffect(() => {
    if (selectedPhoto) trackOpen(selectedPhoto);
  }, [selectedPhoto, trackOpen]);

  // Read from URL only on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const idFromUrl = params.get("photoId");
    if (idFromUrl) setPhotoId(idFromUrl);
  }, []);

  const sliderSettings = useMemo(
    () => ({
      ...sliderDefaults,
      slidesToShow: slides,
      prevArrow: (
        <SliderArrow icon={<LeftOutlined />} label="Previous portfolio photo" />
      ),
      nextArrow: (
        <SliderArrow icon={<RightOutlined />} label="Next portfolio photo" />
      ),
    }),
    [slides],
  );

  const onChangePreview = useCallback(
    (id: string) => {
      if (!id) return;

      const url = new URL(window.location.href);
      url.searchParams.set("photoId", id);
      window.history.replaceState(null, "", url.toString());

      setPhotoId(id);

      track("PhotoViewInStylistPage", {
        photoId: id,
        ownerName: user.name,
        ownerId: user._id,
      });
    },
    [user.name, user._id],
  );

  const onClosePreview = useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.delete("photoId");
    window.history.replaceState(null, "", url.toString());
    setPhotoId(null);
  }, []);

  // Preview uses LARGE variant; grid uses PUBLIC
  const previewItems = useMemo(
    () =>
      photos.map((p) => {
        const src = p.type === "video" ? p.src : getLargeSrc(p.src);
        return {
          ...p,
          src, // use LARGE for preview
        };
      }),
    [photos],
  );

  const previewSettings = useMemo(() => {
    const currentIndex = photoId
      ? photos.findIndex((photo) => photo._id === photoId)
      : 0;

    return {
      open: currentIndex >= 0 && !!photoId,
      current: currentIndex !== -1 ? currentIndex : 0,
      actionsRender: () => null,
      imageRender: (
        originNode: React.ReactNode,
        { current }: { current: number },
      ) => {
        const currentPhoto = previewItems[current];
        if (currentPhoto?.type === "video") {
          return (
            <ReactPlayer
              url={currentPhoto.src}
              playing
              controls
              muted
              width="100%"
              height="100%"
            />
          );
        }
        return originNode;
      },
      onChange: (current: number) => {
        const selectedPhoto = photos[current];
        if (selectedPhoto?._id) onChangePreview(selectedPhoto._id);
      },
      onOpenChange: (open: boolean) => {
        if (!open) onClosePreview();
      },
    };
  }, [photoId, photos, previewItems, onChangePreview, onClosePreview]);

  return (
    <div
      ref={root}
      id="DisplaySlider"
      className={`is-relative${fullBleed ? " is-full-bleed" : ""}`}
      style={{ minHeight: "180px" }}
    >
      <Image.PreviewGroup items={previewItems} preview={previewSettings}>
        <Carousel {...sliderSettings}>
          {photos.map((photo, index) =>
            photo.type === "video" ? (
              <button
                data-work-photo-id={photo.workPhotoId}
                data-salon-id={photo.salonId}
                key={photo._id}
                type="button"
                className={`slider-video-slide ${styles.mediaButton}`}
                onClick={() => onChangePreview(photo._id)}
                aria-label={`Open ${photo.alt || "portfolio video"} in gallery`}
              >
                <LazyVideo
                  url={photo.src}
                  poster={photo.variants?.[0]}
                  posterLoading={index < slides ? "eager" : "lazy"}
                  reducedMotionControls={false}
                  width="100%"
                  height="100%"
                />
              </button>
            ) : (
              <button
                data-work-photo-id={photo.workPhotoId}
                data-salon-id={photo.salonId}
                key={photo._id}
                type="button"
                className={styles.mediaButton}
                onClick={() => onChangePreview(photo._id)}
                aria-label={`Open ${photo.alt || "portfolio photo"} in gallery`}
              >
                <Image
                  src={photo.src ? photo.src : "/logo.png"}
                  alt={photo.alt || "Photo"}
                  width="100%"
                  height="100%"
                  preview={false}
                  loading={index < slides ? "eager" : "lazy"}
                />
              </button>
            ),
          )}
        </Carousel>
      </Image.PreviewGroup>
    </div>
  );
};

export default DisplaySlider;
